import { Maybe, rd } from '@passionware/monads';
import { Globe2 } from 'lucide-react';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxQueryRenderer,
  ComboboxTrigger
} from 'v5/design-system/Combobox';
import { InputSize, InputVariant } from 'v5/design-system/Input';
import { selectTriggerVariants } from 'v5/design-system/Select';
import {
  getAllCountries,
  getAllCountryNames,
  getCountryCallingCode,
  getCountryFlag
} from './country';
import { CountryCode } from 'libphonenumber-js';

export interface CountryInputProps {
  className?: string;
  showCallingCode?: boolean;
  formProps?: {
    onChange?: (value: CountryCode | undefined) => void;
    onBlur?: () => void;
    value?: Maybe<CountryCode>;
    disabled?: boolean;
    name?: string;
    ref?: React.Ref<HTMLInputElement>;
  };
  /** `headless`: no chrome of its own, inside a control that has it (a row of an input group). */
  variant?: InputVariant | 'headless';
  size?: InputSize;
  hasError?: boolean;
  /** The trigger's name when nothing around it labels it. */
  'aria-label'?: string;
  placeholder?: string;
  readOnly?: boolean;
  emptyDisplay?: string;
}

/**
 * A country selection component that integrates with the v5 design system.
 *
 * @experimental It has limited API since we are not sure how it will be used
 */
export function CountryInput({
  formProps = {},
  showCallingCode = true,
  className,
  variant = 'regular',
  size = 'md',
  hasError = false,
  placeholder,
  readOnly = false,
  emptyDisplay,
  'aria-label': ariaLabel
}: CountryInputProps) {
  const { t } = useTranslation('common-widgets');
  const effectivePlaceholder = placeholder || t('CountryInput.placeholder');

  // Common countries to show first
  const COMMON_COUNTRIES: CountryCode[] = [
    'US',
    'GB',
    'DE',
    'CN',
    'FR',
    'CA',
    'AU',
    'JP'
  ];

  const allCountries = useMemo(() => getAllCountries(), []);
  const countryNames = useMemo(() => getAllCountryNames(), []);

  const { commonCountries, otherCountries } = useMemo(() => {
    const common = COMMON_COUNTRIES.filter(c => allCountries.includes(c));
    const others = allCountries.filter(c => !COMMON_COUNTRIES.includes(c));
    return { commonCountries: common, otherCountries: others };
  }, [allCountries]);

  const selectedCountry = formProps.value as CountryCode | undefined;

  const renderCountryItem = (country: CountryCode) => {
    const content = (
      <div className="flex items-center gap-2">
        {getCountryFlag(country)}
        <span className="min-w-12 text-right tabular-nums">
          +{getCountryCallingCode(country)}
        </span>
        <span className="truncate">{countryNames[country.toUpperCase()]}</span>
      </div>
    );

    return (
      <ComboboxItem
        key={country}
        value={country}
        onSelect={() => {
          formProps.onChange?.(country);
        }}
        closeOnSelect
      >
        {content}
      </ComboboxItem>
    );
  };

  return (
    <Combobox disabled={formProps.disabled || readOnly}>
      <ComboboxTrigger asChild>
        <button
          type="button"
          className={cn(
            selectTriggerVariants({ variant, size }),
            hasError && variant === 'regular' && 'border-red-500',
            readOnly && 'pointer-events-none cursor-default',
            'w-auto',
            className
          )}
          onBlur={formProps.onBlur}
          aria-label={ariaLabel}
          aria-readonly={readOnly ? 'true' : undefined}
          tabIndex={readOnly ? -1 : undefined}
        >
          <div className="value-container w-full flex flex-row items-center gap-1 truncate">
            {selectedCountry ? (
              <>
                {getCountryFlag(selectedCountry)}
                {showCallingCode && (
                  <span>+{getCountryCallingCode(selectedCountry)}</span>
                )}
              </>
            ) : (
              <>
                <Globe2 className="size-4" />
                {emptyDisplay && <span className="ml-1">{emptyDisplay}</span>}
              </>
            )}
          </div>
          {!readOnly && (
            <svg
              className="size-5 shrink-0 opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </button>
      </ComboboxTrigger>
      <ComboboxContent className="p-0 max-h-[400px]">
        <ComboboxInput placeholder={effectivePlaceholder} />
        <ComboboxList>
          <ComboboxQueryRenderer
            emptyContent={t('CountryInput.emptyCountries')}
          >
            {query => {
              const queryLower = query.toLowerCase();

              const matchesQuery = (country: CountryCode) =>
                !queryLower ||
                countryNames[country.toUpperCase()]
                  ?.toLowerCase()
                  .includes(queryLower) ||
                country.toLowerCase().includes(queryLower) ||
                getCountryCallingCode(country).includes(queryLower);

              const commonFiltered = commonCountries.filter(matchesQuery);
              const othersFiltered = otherCountries.filter(matchesQuery);
              const hasResults =
                commonFiltered.length + othersFiltered.length > 0;

              if (!hasResults) {
                return rd.of(null);
              }

              return rd.of(
                <>
                  {commonFiltered.length > 0 && (
                    <ComboboxGroup
                      heading={
                        query ? undefined : t('CountryInput.groupCommon')
                      }
                    >
                      {commonFiltered.map(renderCountryItem)}
                    </ComboboxGroup>
                  )}
                  {othersFiltered.length > 0 && (
                    <ComboboxGroup
                      heading={
                        query ? undefined : t('CountryInput.groupAllCountries')
                      }
                    >
                      {othersFiltered.map(renderCountryItem)}
                    </ComboboxGroup>
                  )}
                </>
              );
            }}
          </ComboboxQueryRenderer>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
